// Loan Compliance Module v1.0 PR 9 — bindings adapter that lets the shared
// Track 3 attorney workbench drive either a loan- or pool-scoped engagement.
// The workbench renders generic markup against the normalized shapes
// declared here; the two factory hooks (loan + pool) wire the right
// underlying TanStack-Query hooks and normalize each scope's DTO into the
// shared shape.

import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import {
  useCreateLegalReviewMemo,
  useCreatePoolLegalReviewMemo,
  useFinalizeLoanLegalReview,
  useFinalizeSpvPoolLegalReview,
  useGenerateDraftLlcr,
  useGenerateDraftPlcr,
  useGenerateOfficerCertSupport,
  useLegalReviewMemos,
  useLoanLegalReview,
  usePoolLegalReviewMemos,
  useRecordModuleConclusion,
  useRecordPoolModuleConclusion,
  useRecordPoolSupervisorAcceptance,
  useRecordSupervisorAcceptance,
  useReissueLoanLegalReview,
  useReissueSpvPoolLegalReview,
  useSignLegalReviewMemo,
  useSignPoolLegalReviewMemo,
  useSpvPoolLegalReview,
  useSubmitForReview,
  useSubmitPoolForReview,
  useUpdatePoolScopingMemo,
  useUpdateScopingMemo,
} from "@/hooks/compliance/useLegalReview";
import {
  isFullPoolReview,
  isFullReview,
  LLCR_CERTIFICATION_DEFINITION_ID,
  LOAN_LEGAL_REVIEW_MODULES,
  PLCR_CERTIFICATION_DEFINITION_ID,
  POOL_LEGAL_REVIEW_MODULES,
} from "@/api/compliance/legalReviewTypes";
import type {
  CreateLegalReviewMemoRequest,
  CreatePoolLegalReviewMemoRequest,
  FinalizeLoanLegalReviewRequest,
  FinalizePoolLegalReviewRequest,
  LegalReviewConclusion,
  LegalReviewMemoDto,
  LegalReviewScope,
  LoanLegalReviewDto,
  LoanLegalReviewModuleDto,
  LoanLegalReviewStatus,
  LoanLegalReviewSummaryDto,
  PoolLegalReviewDto,
  PoolLegalReviewMemoDto,
  PoolLegalReviewModuleDto,
  PoolLegalReviewSummaryDto,
  PoolSupervisorAcceptanceRequest,
  RecordModuleConclusionRequest,
  RecordPoolModuleConclusionRequest,
  ReissueLoanLegalReviewRequest,
  ReissuePoolLegalReviewRequest,
  SignLegalReviewMemoRequest,
  SignPoolLegalReviewMemoRequest,
  SupervisorAcceptanceRequest,
  UpdatePoolScopingMemoRequest,
  UpdateScopingMemoRequest,
} from "@/api/compliance/legalReviewTypes";

// =========================================================================
// Normalized shapes (what the workbench actually renders against).
// =========================================================================

export interface NormalizedReviewModule {
  id: string;
  reviewId: string;
  moduleCode: string;
  moduleName: string;
  workingNotes: string | null;
  conclusion: LegalReviewConclusion;
  conclusionNote: string | null;
  completedByUserId: string | null;
  completedAt: string | null;
  relatedComplianceExceptionId: string | null;
}

export interface NormalizedReview {
  scope: LegalReviewScope;
  id: string;
  subjectId: string;
  auditYear: number;
  attorneyUserId: string;
  supervisorUserId: string | null;
  status: LoanLegalReviewStatus;
  scopingMemo: string | null;
  finalReportSubmissionId: string | null;
  officerCertSupportReportSubmissionId: string | null;
  certificationRecordId: string | null;
  signedByUserId: string | null;
  signedAt: string | null;
  reissueOfId: string | null;
  privilegeProtected: boolean;
  modules: NormalizedReviewModule[];
  createdAt: string;
  createdByUserId: string | null;
  versionId: number;
}

export interface NormalizedMemo {
  id: string;
  attorneyUserId: string;
  subject: string;
  body: string;
  relatedEventType: string | null;
  relatedEventId: string | null;
  signedAt: string | null;
  certificationRecordId: string | null;
  createdAt: string;
}

export interface NormalizedSignMemoRequest {
  certificationDefinitionId: string;
}

export interface NormalizedCreateMemoRequest {
  subject: string;
  body: string;
  relatedEventType?: string | null;
  relatedEventId?: string | null;
}

export interface NormalizedModuleConclusionRequest {
  moduleCode: string;
  conclusion: LegalReviewConclusion;
  conclusionNote?: string | null;
  workingNotes?: string | null;
  reviewedDocuments?: Array<{ documentId: string; documentVersion: string }>;
  exceptionSeverity?:
    | "Critical"
    | "Material"
    | "Process"
    | "Observation"
    | null;
  exceptionTitle?: string | null;
  exceptionDescription?: string | null;
  programDocumentCitation?: string | null;
}

export interface NormalizedSupervisorAcceptanceRequest {
  supervisorUserId: string;
}

export interface NormalizedReissueRequest {
  reason: string;
}

export interface NormalizedFinalizeRequest {
  certificationDefinitionId: string;
}

export interface NormalizedScopingRequest {
  scopingMemo: string;
}

type MutationResult<TVars> = UseMutationResult<unknown, unknown, TVars>;

export interface LegalReviewBindings {
  scope: LegalReviewScope;
  subjectId: string;
  reviewId: string;
  /** Display label for the subject (e.g. "Loan loan-1" or "Pool pool-1"). */
  subjectLabel: string;
  /** Route to the subject detail page (loan or pool). */
  subjectDetailPath: string;
  /** What workbench should show in the back-link breadcrumb. */
  subjectBreadcrumbLabel: string;
  modulesCatalog: ReadonlyArray<{ code: string; name: string }>;
  /** e.g. "3.1–3.9" or "3.10–3.15". */
  moduleRangeLabel: string;
  /** Short report code displayed on the report card (e.g. "LLCR", "PLCR"). */
  reportShortName: string;
  /** Long name shown on the report card title. */
  reportLongName: string;
  /** One-line description shown under the report card title. */
  reportDescription: string;
  certificationDefinitionId: string;
  /** Pool-only feature flag. */
  supportsOfficerCertSupport: boolean;

  /** Result of the GET /reviews/{id} query (or pool equivalent). The
   *  workbench inspects status + data to decide between loading / error /
   *  summary-only / full. Returns `null` for `data` when the caller saw the
   *  summary projection. */
  reviewQuery: UseQueryResult<NormalizedReview | null>;
  /** When the caller got a summary projection instead of a full DTO. */
  isSummaryOnly: boolean;
  memosQuery: UseQueryResult<NormalizedMemo[]>;

  updateScopingMemo: MutationResult<NormalizedScopingRequest>;
  recordModuleConclusion: MutationResult<NormalizedModuleConclusionRequest>;
  generateDraft: MutationResult<void>;
  /** Pool-only. Undefined for loan scope. */
  generateOfficerCertSupport?: MutationResult<void>;
  submitForReview: MutationResult<void>;
  recordSupervisorAcceptance: MutationResult<NormalizedSupervisorAcceptanceRequest>;
  finalize: MutationResult<NormalizedFinalizeRequest>;
  reissue: MutationResult<NormalizedReissueRequest>;
  createMemo: MutationResult<NormalizedCreateMemoRequest>;
  /** A custom-hook factory used by per-memo rows. Each row calls this once
   *  for its own memo id so the React mutation lifecycle is co-located. */
  useSignMemoMutation: (memoId: string) => MutationResult<NormalizedSignMemoRequest>;
}

// =========================================================================
// Normalizers
// =========================================================================

function normalizeLoanModule(
  m: LoanLegalReviewModuleDto,
): NormalizedReviewModule {
  return {
    id: m.id,
    reviewId: m.loanLegalReviewId,
    moduleCode: m.moduleCode,
    moduleName: m.moduleName,
    workingNotes: m.workingNotes,
    conclusion: m.conclusion,
    conclusionNote: m.conclusionNote,
    completedByUserId: m.completedByUserId,
    completedAt: m.completedAt,
    relatedComplianceExceptionId: m.relatedComplianceExceptionId,
  };
}

function normalizePoolModule(
  m: PoolLegalReviewModuleDto,
): NormalizedReviewModule {
  return {
    id: m.id,
    reviewId: m.poolLegalReviewId,
    moduleCode: m.moduleCode,
    moduleName: m.moduleName,
    workingNotes: m.workingNotes,
    conclusion: m.conclusion,
    conclusionNote: m.conclusionNote,
    completedByUserId: m.completedByUserId,
    completedAt: m.completedAt,
    relatedComplianceExceptionId: m.relatedComplianceExceptionId,
  };
}

export function normalizeLoanReview(r: LoanLegalReviewDto): NormalizedReview {
  return {
    scope: "loan",
    id: r.id,
    subjectId: r.loanId,
    auditYear: r.auditYear,
    attorneyUserId: r.attorneyUserId,
    supervisorUserId: r.supervisorUserId,
    status: r.status,
    scopingMemo: r.scopingMemo,
    finalReportSubmissionId: r.finalLlcrReportSubmissionId,
    officerCertSupportReportSubmissionId: null,
    certificationRecordId: r.certificationRecordId,
    signedByUserId: r.signedByUserId,
    signedAt: r.signedAt,
    reissueOfId: r.reissueOfId,
    privilegeProtected: r.privilegeProtected,
    modules: r.modules.map(normalizeLoanModule),
    createdAt: r.createdAt,
    createdByUserId: r.createdByUserId,
    versionId: r.versionId,
  };
}

export function normalizePoolReview(r: PoolLegalReviewDto): NormalizedReview {
  return {
    scope: "pool",
    id: r.id,
    subjectId: r.spvPoolId,
    auditYear: r.auditYear,
    attorneyUserId: r.attorneyUserId,
    supervisorUserId: r.supervisorUserId,
    status: r.status,
    scopingMemo: r.scopingMemo,
    finalReportSubmissionId: r.finalPlcrReportSubmissionId,
    officerCertSupportReportSubmissionId: r.officerCertSupportReportSubmissionId,
    certificationRecordId: r.certificationRecordId,
    signedByUserId: r.signedByUserId,
    signedAt: r.signedAt,
    reissueOfId: r.reissueOfId,
    privilegeProtected: r.privilegeProtected,
    modules: r.modules.map(normalizePoolModule),
    createdAt: r.createdAt,
    createdByUserId: r.createdByUserId,
    versionId: r.versionId,
  };
}

function normalizeLoanMemo(m: LegalReviewMemoDto): NormalizedMemo {
  return {
    id: m.id,
    attorneyUserId: m.attorneyUserId,
    subject: m.subject,
    body: m.body,
    relatedEventType: m.relatedEventType,
    relatedEventId: m.relatedEventId,
    signedAt: m.signedAt,
    certificationRecordId: m.certificationRecordId,
    createdAt: m.createdAt,
  };
}

function normalizePoolMemo(m: PoolLegalReviewMemoDto): NormalizedMemo {
  return {
    id: m.id,
    attorneyUserId: m.attorneyUserId,
    subject: m.subject,
    body: m.body,
    relatedEventType: m.relatedEventType,
    relatedEventId: m.relatedEventId,
    signedAt: m.signedAt,
    certificationRecordId: m.certificationRecordId,
    createdAt: m.createdAt,
  };
}

// =========================================================================
// Factories
// =========================================================================

export function useLoanLegalReviewBindings(
  loanId: string,
  reviewId: string,
): LegalReviewBindings {
  const reviewQuery = useLoanLegalReview(loanId, reviewId);
  const memosQuery = useLegalReviewMemos(loanId);

  const updateScopingMemo = useUpdateScopingMemo(loanId, reviewId);
  const recordModuleConclusion = useRecordModuleConclusion(loanId, reviewId);
  const generateDraft = useGenerateDraftLlcr(loanId, reviewId);
  const submitForReview = useSubmitForReview(loanId, reviewId);
  const recordSupervisorAcceptance = useRecordSupervisorAcceptance(
    loanId,
    reviewId,
  );
  const finalize = useFinalizeLoanLegalReview(loanId, reviewId);
  const reissue = useReissueLoanLegalReview(loanId, reviewId);
  const createMemo = useCreateLegalReviewMemo(loanId);

  const raw = reviewQuery.data;
  const isFull =
    raw !== undefined &&
    isFullReview(raw as LoanLegalReviewDto | LoanLegalReviewSummaryDto);
  const normalized: NormalizedReview | null = isFull
    ? normalizeLoanReview(raw as LoanLegalReviewDto)
    : null;

  // Project the review query into a normalized shape for the workbench. We
  // preserve loading / error flags but swap the data so consumers don't
  // need to know about the raw DTO.
  const projectedReviewQuery = {
    ...reviewQuery,
    data: normalized,
  } as unknown as UseQueryResult<NormalizedReview | null>;

  const projectedMemosQuery = {
    ...memosQuery,
    data: (memosQuery.data ?? []).map(normalizeLoanMemo),
  } as unknown as UseQueryResult<NormalizedMemo[]>;

  return {
    scope: "loan",
    subjectId: loanId,
    reviewId,
    subjectLabel: `Loan ${loanId}`,
    subjectDetailPath: `/portfolio-admin/loans/${loanId}?tab=legal-review`,
    subjectBreadcrumbLabel: "loan",
    modulesCatalog: LOAN_LEGAL_REVIEW_MODULES,
    moduleRangeLabel: "3.1–3.9",
    reportShortName: "LLCR",
    reportLongName: "LLCR document",
    reportDescription:
      "The Loan-Level Legal Compliance Report rendered from the recorded conclusions.",
    certificationDefinitionId: LLCR_CERTIFICATION_DEFINITION_ID,
    supportsOfficerCertSupport: false,
    reviewQuery: projectedReviewQuery,
    isSummaryOnly: !isFull && raw !== undefined,
    memosQuery: projectedMemosQuery,
    updateScopingMemo: updateScopingMemo as unknown as MutationResult<NormalizedScopingRequest>,
    recordModuleConclusion:
      recordModuleConclusion as unknown as MutationResult<NormalizedModuleConclusionRequest>,
    generateDraft: generateDraft as unknown as MutationResult<void>,
    submitForReview: submitForReview as unknown as MutationResult<void>,
    recordSupervisorAcceptance:
      recordSupervisorAcceptance as unknown as MutationResult<NormalizedSupervisorAcceptanceRequest>,
    finalize: finalize as unknown as MutationResult<NormalizedFinalizeRequest>,
    reissue: reissue as unknown as MutationResult<NormalizedReissueRequest>,
    createMemo: createMemo as unknown as MutationResult<NormalizedCreateMemoRequest>,
    useSignMemoMutation: (memoId: string) =>
      useSignLegalReviewMemo(
        loanId,
        memoId,
      ) as unknown as MutationResult<NormalizedSignMemoRequest>,
  };
}

export function useSpvPoolLegalReviewBindings(
  poolId: string,
  reviewId: string,
): LegalReviewBindings {
  const reviewQuery = useSpvPoolLegalReview(poolId, reviewId);
  const memosQuery = usePoolLegalReviewMemos(poolId);

  const updateScopingMemo = useUpdatePoolScopingMemo(poolId, reviewId);
  const recordModuleConclusion = useRecordPoolModuleConclusion(
    poolId,
    reviewId,
  );
  const generateDraft = useGenerateDraftPlcr(poolId, reviewId);
  const generateOfficerCertSupport = useGenerateOfficerCertSupport(
    poolId,
    reviewId,
  );
  const submitForReview = useSubmitPoolForReview(poolId, reviewId);
  const recordSupervisorAcceptance = useRecordPoolSupervisorAcceptance(
    poolId,
    reviewId,
  );
  const finalize = useFinalizeSpvPoolLegalReview(poolId, reviewId);
  const reissue = useReissueSpvPoolLegalReview(poolId, reviewId);
  const createMemo = useCreatePoolLegalReviewMemo(poolId);

  const raw = reviewQuery.data;
  const isFull =
    raw !== undefined &&
    isFullPoolReview(raw as PoolLegalReviewDto | PoolLegalReviewSummaryDto);
  const normalized: NormalizedReview | null = isFull
    ? normalizePoolReview(raw as PoolLegalReviewDto)
    : null;

  const projectedReviewQuery = {
    ...reviewQuery,
    data: normalized,
  } as unknown as UseQueryResult<NormalizedReview | null>;

  const projectedMemosQuery = {
    ...memosQuery,
    data: (memosQuery.data ?? []).map(normalizePoolMemo),
  } as unknown as UseQueryResult<NormalizedMemo[]>;

  return {
    scope: "pool",
    subjectId: poolId,
    reviewId,
    subjectLabel: `Pool ${poolId}`,
    subjectDetailPath: `/capital-markets/pools/${poolId}?tab=legal-review`,
    subjectBreadcrumbLabel: "pool",
    modulesCatalog: POOL_LEGAL_REVIEW_MODULES,
    moduleRangeLabel: "3.10–3.15",
    reportShortName: "PLCR",
    reportLongName: "PLCR document",
    reportDescription:
      "The Pool-Level Legal Compliance Report rendered from the recorded conclusions.",
    certificationDefinitionId: PLCR_CERTIFICATION_DEFINITION_ID,
    supportsOfficerCertSupport: true,
    reviewQuery: projectedReviewQuery,
    isSummaryOnly: !isFull && raw !== undefined,
    memosQuery: projectedMemosQuery,
    updateScopingMemo: updateScopingMemo as unknown as MutationResult<NormalizedScopingRequest>,
    recordModuleConclusion:
      recordModuleConclusion as unknown as MutationResult<NormalizedModuleConclusionRequest>,
    generateDraft: generateDraft as unknown as MutationResult<void>,
    generateOfficerCertSupport:
      generateOfficerCertSupport as unknown as MutationResult<void>,
    submitForReview: submitForReview as unknown as MutationResult<void>,
    recordSupervisorAcceptance:
      recordSupervisorAcceptance as unknown as MutationResult<NormalizedSupervisorAcceptanceRequest>,
    finalize: finalize as unknown as MutationResult<NormalizedFinalizeRequest>,
    reissue: reissue as unknown as MutationResult<NormalizedReissueRequest>,
    createMemo: createMemo as unknown as MutationResult<NormalizedCreateMemoRequest>,
    useSignMemoMutation: (memoId: string) =>
      useSignPoolLegalReviewMemo(
        poolId,
        memoId,
      ) as unknown as MutationResult<NormalizedSignMemoRequest>,
  };
}

// =========================================================================
// Re-export request types for ergonomics in the workbench file.
// =========================================================================

export type {
  CreateLegalReviewMemoRequest,
  CreatePoolLegalReviewMemoRequest,
  FinalizeLoanLegalReviewRequest,
  FinalizePoolLegalReviewRequest,
  PoolSupervisorAcceptanceRequest,
  RecordModuleConclusionRequest,
  RecordPoolModuleConclusionRequest,
  ReissueLoanLegalReviewRequest,
  ReissuePoolLegalReviewRequest,
  SignLegalReviewMemoRequest,
  SignPoolLegalReviewMemoRequest,
  SupervisorAcceptanceRequest,
  UpdatePoolScopingMemoRequest,
  UpdateScopingMemoRequest,
};
